import { defineStore } from 'pinia'
import { $api } from '@/utils/api'
import { getI18n } from '@/plugins/i18n'

interface Language {
  id: number
  name: string
  code: string
  is_active: boolean
  is_default: boolean
  tenant_id: number | null
}

interface I18nLanguage {
  id: number
  label: string
  i18nLang: string
  isRTL: boolean
}

const setByPath = (obj: any, path: string[], value: any) => {
  let curr = obj
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i]
    if (!curr[k] || typeof curr[k] !== 'object')
      curr[k] = {}
    curr = curr[k]
  }
  const leaf = path[path.length - 1]
  if (curr[leaf] && typeof curr[leaf] === 'object')
    return

  curr[leaf] = value
}

export const useLanguageStore = defineStore('language', {
  state: () => ({
    languages: [] as I18nLanguage[],
    loading: false,
  }),
  actions: {
    async fetchLanguages() {
      this.loading = true
      try {
        const data = await $api<Language[]>('/languages')

        const rtlCodes = ['ar', 'fa', 'he', 'ur']

        this.languages = data.map(lang => ({
          id: lang.id,
          label: lang.name,
          i18nLang: lang.code,
          isRTL: rtlCodes.includes(lang.code),
        }))
      }
      catch (err) {
        console.error('Failed to fetch languages:', err)
      }
      finally {
        this.loading = false
      }
    },

    async fetchTranslations(locale: string) {
      if (!this.languages.length)
        await this.fetchLanguages()

      const lang = this.languages.find(l => l.i18nLang === locale)
      if (!lang) {
        console.warn(`Language ${locale} not found in available languages`)

        return
      }

      try {
        const translations = await $api<any[]>(`/languages/${lang.id}/translations`)

        const messages = translations.reduce((acc, curr) => {
          if (typeof curr?.key !== 'string')
            return acc

          const key = curr.key.trim()
          const text = typeof curr?.text === 'string' ? curr.text : String(curr?.text ?? '')

          if (!key)
            return acc

          if (key === '$vuetify')
            return acc

          if (key.includes('.')) {
            setByPath(acc, key.split('.'), text)

            return acc
          }

          acc[key] = text

          return acc
        }, {} as Record<string, any>)

        // Update i18n instance
        const i18n = getI18n()

        const currentLocaleMessage: any = i18n.global.getLocaleMessage(locale)
        const enBase: any = i18n.global.getLocaleMessage('en')
        if (locale !== 'en' && !currentLocaleMessage?.$vuetify && enBase?.$vuetify)
          i18n.global.mergeLocaleMessage(locale, { $vuetify: enBase.$vuetify })

        i18n.global.mergeLocaleMessage(locale, messages)

        // Set locale if not already set (optional, but good practice to ensure it's active)
        if (i18n.global.locale.value !== locale)
          i18n.global.locale.value = locale
      }
      catch (err) {
        console.error(`Failed to fetch translations for ${locale}:`, err)
      }
    },
  },
})
